"use client";

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { formatFloatFor, formatInteger } from "@jasperlabs/number-format";

import { cn } from "../../lib/utils";

export type ProgressProps = Omit<
	React.ComponentProps<typeof ProgressPrimitive.Root>,
	"value"
> & {
	value?: number | string | null;
	/**
	 * When true, the text label shows the real value (e.g. 108%) while the bar stays capped at 100%.
	 * Useful for over-subscribed / over-allocated scenarios.
	 */
	allowOverflow?: boolean;
	/**
	 * Visual treatment of the bar indicator. `"danger"` paints the indicator with `bg-danger`
	 * (e.g. for over-allocated / over-called scenarios); defaults to the standard `bg-success`.
	 */
	variant?: "default" | "danger";
	/** Digits after the decimal point in the label. Defaults to a whole percent. */
	precision?: number;
};

/**
 * @param value - the progress value. Non-finite or missing values are treated as 0; negative
 *   values as 0; values above 100 visually fill the bar. By default the text label is also clamped
 *   to 100%.
 * @param allowOverflow - when true, the text label shows the real value (e.g. 108%) while the
 *   bar stays capped at 100%. Useful for over-subscribed / over-allocated scenarios.
 * @param variant - `"danger"` paints the indicator with `bg-danger`; defaults to `bg-success`.
 * @param precision - digits after the decimal point in the label; defaults to a whole percent.
 */
function Progress({
	className,
	value,
	allowOverflow = false,
	variant = "default",
	precision = 0,
	...props
}: ProgressProps) {
	const numeric = Number(value);
	const safeValue = Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
	const percent = Math.min(100, safeValue);
	const rawLabel = allowOverflow ? safeValue : percent;
	const labelValue =
		typeof value === "string" && numeric === rawLabel ? value : rawLabel;
	const label =
		precision > 0
			? formatFloatFor({ precision, rounding: "halfUp" })(labelValue)
			: formatInteger(Math.round(rawLabel));

	return (
		<div>
			<ProgressPrimitive.Root
				data-slot="progress"
				value={percent}
				className={cn(
					"bg-[#E6E8F0] relative h-1.5 w-full overflow-hidden rounded-full",
					className,
				)}
				{...props}
			>
				<ProgressPrimitive.Indicator
					data-slot="progress-indicator"
					className={cn(
						variant === "danger" ? "bg-danger" : "bg-success",
						"h-full w-full flex-1 transition-all",
					)}
					style={{ transform: `translateX(-${100 - percent}%)` }}
				/>
			</ProgressPrimitive.Root>
			<span className="text-xs text-neutral-600">{label}%</span>
		</div>
	);
}

export { Progress };
